import React from 'react'

import Section from '../Section'
import Header, { Title } from '../Header'
import Separator from '../Separator'
import { Tooltip } from '@sketch/components'
import {
  Container,
  ContainerWithoutLink,
  LinkStyled,
  VersionLinkStyled,
  Body,
  Preview,
  Chevron,
  Name,
  Path,
  StyledSkeleton,
} from './TextOrLayerStyles.styles'

import { useGetComponentWithUuid, isTextStyle } from '../Inspector/operations'
import { routes, useFlag } from '@sketch/modules-common'
import { useResponsiveImage } from 'modules/shares/ComponentsWebView/hooks'
import { useComponentsState } from 'modules/shares/components/ComponentsStateContext'

import { formatPath } from 'modules/shares/Inspector/Sidebar/utils'

import { InspectorData, TextOrLayerStyle } from 'modules/shares/Inspector/types'
import { Layer } from 'modules/shares/types'
import { VersionFragment } from '@sketch/gql-types'
import { File } from 'modules/shares/ComponentsWebView/types'

// This is a valid use case to narrow down ComponentInfoFragment type
// eslint-disable-next-line no-restricted-imports
import { LayerStyle } from '@sketch/gql-types/expansive'

/**
 * TYPES
 */
interface TextOrLayerStylesProps {
  shareIdentifier: string
  currentVersion: VersionFragment
  styles: InspectorData['textStyles'] | InspectorData['layerStyles']
  layer: Layer
  title: string
}

/**
 * UTILS
 */
const getPath = (
  style: TextOrLayerStyle,
  externalShareName?: string | null
): { name: string; path: string } => {
  const textStylePath = style.name.split('/')
  const name = textStylePath.pop()

  const documentName = style.isForeign ? externalShareName : 'This Document'

  return {
    name: name?.trim() ?? '',
    path: formatPath([documentName || '', ...textStylePath]),
  }
}

/**
 * HELPER COMPONENTS
 */
const LinkUnavailable: React.FC = ({ children }) => (
  <Tooltip
    placement="top"
    content="This style belongs to a Library that is unavailable"
  >
    {children}
  </Tooltip>
)

/**
 * MAIN COMPONENT
 */
const TextOrLayerStyles = ({
  shareIdentifier,
  currentVersion,
  styles,
  title,
  layer,
}: TextOrLayerStylesProps) => {
  // TODO: Remove FF when released
  // https://github.com/sketch-hq/Cloud/issues/17734
  const isComponentsOnDemandOn = useFlag('component-ingest-on-demand')

  const componentsState = useComponentsState()

  const style = styles[layer.sharedStyleID]

  const {
    loading,
    shareID,
    externalShareName,
    versionShortId,
    component,
    componentUnavailable,
    searchParams,
  } = useGetComponentWithUuid({
    componentId: style?.isForeign ? style?.remoteId : style?.id,
    externalDocumentId: style?.documentID,
    externalDocumentName: style?.documentName,
    isForeign: style?.isForeign,
    shareIdentifier,
    currentVersion,
  })

  const previewProps = useResponsiveImage({
    name: component?.name || '',
    files: (component as LayerStyle)?.files as File[],
  })

  if (!style) {
    return null
  }

  const { name, path } = getPath(
    style,
    // externalShareName has wrong value when there is an error with the query,
    // that's why we need to provide a fallback using style.documentName
    componentUnavailable ? style.documentName : externalShareName
  )
  const sectionCopyValue = style.name

  const isDirty = !!style.value.isDirty || !!layer.style?.isDirty

  const body = (
    <Body>
      <Name isDirty={isDirty}>
        {name}
        {isDirty && `*`}
      </Name>
      <Path>{path}</Path>
    </Body>
  )

  const componentWithoutLink = <Container>{body}</Container>

  const getPathnameForLatestShareVersion = () =>
    style.value.text
      ? routes.CWV_TEXT_STYLES.create({
          shareID: shareID!,
        })
      : routes.CWV_LAYER_STYLES.create({
          shareID: shareID!,
        })

  const linkExternalComponent = componentUnavailable ? (
    <LinkUnavailable>
      <ContainerWithoutLink data-testid="inspector-component-no-link">
        {body}
      </ContainerWithoutLink>
    </LinkUnavailable>
  ) : (
    <LinkStyled
      data-testid="inspector-component-link-external"
      to={{
        pathname: getPathnameForLatestShareVersion(),
        search: searchParams,
      }}
    >
      <Preview
        isTextStyle={isTextStyle(component)}
        needsContrastingBackground={
          !!(component as LayerStyle)?.needsContrastingBackground
        }
        {...previewProps}
      />
      {body}
      <Chevron />
    </LinkStyled>
  )

  const linkInternalComponent = (
    <VersionLinkStyled
      data-testid="inspector-component-link-internal"
      to={{
        routeKey: style.value.text ? 'CWV_TEXT_STYLES' : 'CWV_LAYER_STYLES',
        routeParams: {
          shareID: shareIdentifier,
          versionShortId,
        },
        search: searchParams,
      }}
    >
      <Preview
        isTextStyle={isTextStyle(component)}
        needsContrastingBackground={
          !!(component as LayerStyle)?.needsContrastingBackground
        }
        {...previewProps}
      />
      {body}
      <Chevron />
    </VersionLinkStyled>
  )

  // Note: when linking to a foreign component we always link to the latest
  // version, but for internal components we want a versioned link
  const wrapperLink = style.isForeign
    ? loading
      ? componentWithoutLink
      : linkExternalComponent
    : linkInternalComponent

  const isIngesting = componentsState === 'PROCESSING'

  return (
    <>
      <Separator />
      <Section data-testid="inspector-sidebar-styles">
        <Header copyValue={sectionCopyValue}>
          <Title>{title}</Title>
        </Header>

        {isComponentsOnDemandOn && isIngesting ? (
          <StyledSkeleton />
        ) : (
          wrapperLink
        )}
      </Section>
    </>
  )
}

export default TextOrLayerStyles
